import { useMemo, useState, useEffect, useRef } from 'react';
import { IMenuDomain } from '../index';
import IconMi from 'components/common/icon/IconMi';
import IconHandler from 'components/common/icon/IconHandler';
import { Box, Stack, Typography } from '@mui/material';
import ExtraMenu from './ExtraMenu';
import { merge } from 'lodash';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from '../styles';
import { useSelector } from 'react-redux';
import AuthRequiredPopup from './AuthRequiredPopup';
import { useHasTokenString } from 'hooks/useHasTokenString';
import { IDomain } from 'store/conciergeSlice';

export default function DomainMenuItem({
    domain,
    selectDomain,
    onSelectPrompt,
    closeMenu,
}: {
    domain: IMenuDomain;
    selectDomain: (name: string) => void;
    onSelectPrompt: (domain: string, prompt: string) => void;
    closeMenu: () => void;
}) {
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;
    const availableDomains = useSelector((state: any) => state.concierge.availableDomains) as IDomain[];

    const styles = useMemo(() => {
        return merge({}, defaultStyles, customStyles?.footerDomainSelector) as typeof defaultStyles;
    }, [customStyles]);

    const className = (domain.menuSelected ? 'selected' : '') + (domain.header ? ' header' : '');
    const [hover, setHover] = useState(false);
    const [showAuthPopup, setShowAuthPopup] = useState(false);
    const [waitingForAuth, setWaitingForAuth] = useState(false);
    const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
    const tokenFieldName = domain.authInfo?.tokenFieldName || '';
    const tokenStatus = useHasTokenString(tokenFieldName);
    const previousTokenStatus = useRef(tokenStatus.hasToken);
    const needsAuth = !!domain.hasAuth && !tokenStatus.hasToken;

    const iconProps = {
        type: domain.icon_ind,
        value: domain.icon_ind === 'upload' ? `data:image/png;base64,${domain.icon_data}` : domain.icon_name,
    };

    const onDomainClick = (event: React.MouseEvent) => {
        if (needsAuth) {
            (event as any).__miCloseDropdown = true;
            setShowAuthPopup(true);
        } else {
            selectDomain(domain.name);
        }
    };

    const handleAuthorize = () => {
        setShowAuthPopup(false);
        if (domain.authInfo?.authUrl) {
            setWaitingForAuth(true);
            window.open(domain.authInfo?.authUrl, `Authentication`, 'width=530,height=530');
        }
    };

    useEffect(() => {
        if (waitingForAuth && !previousTokenStatus.current && tokenStatus.hasToken) {
            setWaitingForAuth(false);
            if (pendingPrompt) {
                onSelectPrompt(domain.name, pendingPrompt);
                setPendingPrompt(null);
            } else {
                selectDomain(domain.name);
            }
        }
        previousTokenStatus.current = tokenStatus.hasToken;
    }, [tokenStatus.hasToken, waitingForAuth, domain.name, selectDomain, pendingPrompt, onSelectPrompt]);

    const handleClosePopup = () => {
        setShowAuthPopup(false);
        setWaitingForAuth(false);
        setPendingPrompt(null);
    };

    const handleAuthClick = () => {
        setShowAuthPopup(true);
    };

    const handleAuthPromptClick = (prompt: string) => {
        setPendingPrompt(prompt);
        setShowAuthPopup(true);
    };

    return (
        <>
            <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
                sx={styles.menuItem}
                className={className}
                onMouseLeave={() => setHover(false)}
                onMouseEnter={() => setHover(true)}
                onClick={onDomainClick}
            >
                <Stack direction={'row'} gap={1}>
                    <Box width={'21px'} height={'21px'} display="flex" justifyContent={'center'} alignItems={'center'}>
                        {needsAuth ? (
                            <Box sx={styles.noCheck} />
                        ) : domain.menuSelected ? (
                            <IconMi icon={'check'} fontSize="16" />
                        ) : hover ? (
                            <Box sx={styles.checkHolder} />
                        ) : (
                            <Box sx={{ width: '16px' }} />
                        )}
                    </Box>
                    <Stack direction={'row'} alignItems={'center'} spacing={1} justifyContent={'flex-start'}>
                        <Box
                            sx={{
                                width: '21px',
                                height: '21px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <IconHandler icon={iconProps} sx={{ fontSize: '16px' }} />
                        </Box>

                        <Typography>{domain.title}</Typography>
                    </Stack>
                </Stack>
                <ExtraMenu
                    domain={domain}
                    onSelectPrompt={onSelectPrompt}
                    needsAuth={needsAuth}
                    closeMenu={closeMenu}
                    onAuthClick={handleAuthClick}
                    onAuthPromptClick={handleAuthPromptClick}
                />
            </Stack>

            <AuthRequiredPopup
                open={showAuthPopup}
                onConfirm={handleAuthorize}
                onHide={handleClosePopup}
                domainTitle={domain.title}
                authInfo={domain.authInfo}
                serviceName={domain.serviceName}
                availableDomains={availableDomains}
            />
        </>
    );
}
